
import {
  computed, defineComponent, onMounted, ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from '@/store';
import ProgressSpinner from 'primevue/progressspinner';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import SegmentationsPanel from '@/components/segmentations/SegmentationsPanel.vue';
import {
  fetchBillingAutoExclusion,
  setBillingAutoExclusion,
} from '@/composables/billing/billingApi';
import { showToastError, showToastSuccess } from '@/helpers';

/**
 * Contenu de la modal « Exclure automatiquement des contacts de la facturation »
 * (offre V5), ouverte depuis le hero de la page abonnement. Réutilise le builder
 * de segment (SegmentationsPanel) SANS shops_lists ; la config est persistée côté
 * payments (`/billing/auto-exclusion`), recalculée SYNCHRONEMENT à l'enregistrement.
 *
 * Le builder est REPLIÉ par défaut (statut + bouton) ; « Modifier » l'ouvre,
 * « Enregistrer » persiste, recalcule, rafraîchit l'overview et ferme la modal.
 */
export default defineComponent({
  name: 'BillingAutoExclusionPanel',

  components: {
    ProgressSpinner,
    SpmButton,
    SegmentationsPanel,
  },

  emits: ['saved', 'close'],

  setup(_props, { emit }) {
    const { t } = useI18n();
    const store = useStore();

    const loading = ref(true);
    const saving = ref(false);
    const editing = ref(false);
    const isActive = ref(false);
    // Config par défaut = un bloc vide (même convention que l'éditeur de liste).
    const segmentsConfig = ref<any>([[{}]]);
    const excludedCount = ref(0);
    const previewCount = ref<number | null>(null);
    const previewLoading = ref(false);

    // Un segment est « défini » dès qu'au moins une condition est renseignée.
    const hasSegment = computed(
      () => Array.isArray(segmentsConfig.value)
        && segmentsConfig.value.some(
          (block: any) => Array.isArray(block)
            && block.some((c: any) => c && Object.keys(c).length > 0),
        ),
    );

    const onCountUpdated = (payload: { count: number | null; loading: boolean }) => {
      previewLoading.value = payload.loading;
      if (!payload.loading) previewCount.value = payload.count;
    };

    const toggleActive = () => {
      isActive.value = !isActive.value;
      // À l'activation sans segment encore défini, on ouvre directement le builder.
      if (isActive.value && !hasSegment.value) editing.value = true;
      if (!isActive.value) editing.value = false;
    };

    const startEditing = () => {
      editing.value = true;
    };

    const reloadConfig = async () => {
      const cfg = await fetchBillingAutoExclusion();
      if (cfg) {
        isActive.value = cfg.isActive;
        if (Array.isArray(cfg.segmentsConfig) && cfg.segmentsConfig.length > 0) {
          segmentsConfig.value = cfg.segmentsConfig;
        }
        excludedCount.value = cfg.excludedCount ?? 0;
      }
    };

    const save = async () => {
      // Règle active mais aucun segment défini → on ouvre le builder.
      if (isActive.value && !hasSegment.value) {
        editing.value = true;
        showToastError(t('billing.autoExclusion.defineSegmentFirst'));
        return;
      }
      // Valide le segment via le store de l'éditeur (panel monté en mode édition).
      if (isActive.value && editing.value) {
        await store.dispatch('segmentationsEditor/validate');
        if (store.getters['segmentationsEditor/getHasError']) {
          showToastError(t('billing.autoExclusion.invalidSegment'));
          return;
        }
      }

      saving.value = true;
      try {
        const res = await setBillingAutoExclusion({
          isActive: isActive.value,
          segmentsConfig: segmentsConfig.value,
        });
        if (!res) {
          showToastError(t('billing.autoExclusion.saveError'));
          return;
        }
        // Le back recalcule SYNCHRONEMENT et renvoie le nombre d'exclus à jour.
        excludedCount.value = res.excludedCount ?? excludedCount.value;
        editing.value = false;
        showToastSuccess(t('billing.autoExclusion.saved'));
        // Rafraîchit l'overview de la page (carte « contacts actifs / non facturables »).
        await store.dispatch('billing/refetchOverview');
        // Ferme la modal (le parent écoute `saved`).
        emit('saved');
      } finally {
        saving.value = false;
      }
    };

    onMounted(async () => {
      await reloadConfig();
      loading.value = false;
    });

    return {
      t,
      loading,
      saving,
      editing,
      isActive,
      segmentsConfig,
      excludedCount,
      previewCount,
      previewLoading,
      hasSegment,
      onCountUpdated,
      toggleActive,
      startEditing,
      save,
    };
  },
});
